import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Edit, Trash2, Clock, Target, BookOpen, Save, X, Copy
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AssessmentManagement = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [selectedAudience, setSelectedAudience] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    grade: 1,
    target_audience: "school_student",
    time_limit_minutes: 30,
    passing_score: 60,
    questions: []
  });

  const [currentQuestion, setCurrentQuestion] = useState({
    question: "",
    options: ["", "", "", ""],
    correct_answer: "",
    category: "interests"
  });

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "school_admin") {
      toast.error("Access denied: Admin only");
      navigate("/");
      return;
    }
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/assessments/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssessments(response.data);
    } catch (error) {
      toast.error("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingAssessment(null);
    setFormData({
      title: "",
      description: "",
      grade: null,
      target_audience: "school_student",
      time_limit_minutes: 30,
      passing_score: 60,
      questions: []
    });
    setIsModalOpen(true);
  };

  const handleEdit = async (assessment) => {
    setEditingAssessment(assessment);
    setFormData({
      title: assessment.title,
      description: assessment.description,
      grade: assessment.grade || null,
      target_audience: assessment.target_audience || "school_student",
      time_limit_minutes: assessment.time_limit_minutes,
      passing_score: assessment.passing_score,
      questions: assessment.questions
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (assessmentId) => {
    if (!confirm("Are you sure you want to delete this assessment?")) return;
    
    try {
      await axios.delete(`${API}/admin/assessments/${assessmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Assessment deleted successfully");
      fetchAssessments();
    } catch (error) {
      toast.error("Failed to delete assessment");
    }
  };

  const handleDuplicate = (assessment) => {
    setEditingAssessment(null);
    setFormData({
      title: `${assessment.title} (Copy)`,
      description: assessment.description,
      grade: assessment.grade || null,
      target_audience: assessment.target_audience || "school_student",
      time_limit_minutes: assessment.time_limit_minutes,
      passing_score: assessment.passing_score,
      questions: assessment.questions
    });
    setIsModalOpen(true);
  };

  const addQuestion = () => {
    if (!currentQuestion.question || currentQuestion.options.some(opt => !opt)) {
      toast.error("Please fill all question fields");
      return;
    }
    
    const newQuestion = {
      ...currentQuestion,
      id: `q${Date.now()}`
    };
    
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    
    // Reset current question
    setCurrentQuestion({
      question: "",
      options: ["", "", "", ""],
      correct_answer: "",
      category: "interests"
    });
    
    toast.success("Question added");
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || formData.questions.length === 0) {
      toast.error("Please fill all required fields and add at least one question");
      return;
    }

    try {
      if (editingAssessment) {
        await axios.patch(
          `${API}/admin/assessments/${editingAssessment.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Assessment updated successfully");
      } else {
        await axios.post(
          `${API}/admin/assessments`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Assessment created successfully");
      }
      
      setIsModalOpen(false);
      fetchAssessments();
    } catch (error) {
      toast.error(`Failed to ${editingAssessment ? 'update' : 'create'} assessment`);
    }
  };

  const filteredAssessments = selectedAudience === "all" 
    ? assessments 
    : assessments.filter(a => a.target_audience === selectedAudience);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Assessment Management</h1>
          <p className="text-gray-600">Create and manage grade-wise assessment tests</p>
        </div>

        {/* Filters and Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Label>Filter by Audience:</Label>
            <Select value={selectedAudience} onValueChange={setSelectedAudience}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select audience" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Audiences</SelectItem>
                <SelectItem value="school_student">School Students</SelectItem>
                <SelectItem value="college_student">College Students</SelectItem>
                <SelectItem value="job_seeker">Job Seekers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={handleCreateNew}>
            <Plus className="w-4 h-4 mr-2" />
            Create New Assessment
          </Button>
        </div>

        {/* Assessments Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading assessments...</p>
          </div>
        ) : filteredAssessments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 mb-4">No assessments found for the selected audience</p>
              <Button onClick={handleCreateNew}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Assessment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAssessments.map((assessment) => (
              <Card key={assessment.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{assessment.title}</CardTitle>
                      <CardDescription className="mt-1">
                        {assessment.description}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Badge variant={assessment.is_active ? "default" : "secondary"}>
                        {assessment.target_audience === "school_student" ? "School Student" : 
                         assessment.target_audience === "college_student" ? "College Student" : "Job Seeker"}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <BookOpen className="w-4 h-4" />
                      <span>{assessment.questions.length} questions</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{assessment.time_limit_minutes} minutes</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Target className="w-4 h-4" />
                      <span>Passing: {assessment.passing_score}%</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleEdit(assessment)}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleDuplicate(assessment)}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Duplicate
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      onClick={() => handleDelete(assessment.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAssessment ? "Edit Assessment" : "Create New Assessment"}
              </DialogTitle>
              <DialogDescription>
                Fill in the details below to {editingAssessment ? "update" : "create"} an assessment
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    placeholder="e.g., Career Aptitude Test - Grade 9"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                    placeholder="Describe the purpose of this assessment"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="target_audience">Target Audience *</Label>
                  <Select 
                    value={formData.target_audience} 
                    onValueChange={(val) => setFormData(prev => ({...prev, target_audience: val}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select audience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="school_student">School Students (All Grades)</SelectItem>
                      <SelectItem value="college_student">College Students</SelectItem>
                      <SelectItem value="job_seeker">Job Seekers / Professionals</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Assessment will be available to all users of this type</p>
                </div>

                <div>
                  <Label htmlFor="time_limit">Time Limit (minutes)</Label>
                  <Input
                    id="time_limit"
                    type="number"
                    value={formData.time_limit_minutes}
                    onChange={(e) => setFormData(prev => ({...prev, time_limit_minutes: parseInt(e.target.value)}))}
                    min={5}
                    max={180}
                  />
                </div>

                <div>
                  <Label htmlFor="passing_score">Passing Score (%)</Label>
                  <Input
                    id="passing_score"
                    type="number"
                    value={formData.passing_score}
                    onChange={(e) => setFormData(prev => ({...prev, passing_score: parseInt(e.target.value)}))}
                    min={0}
                    max={100}
                  />
                </div>
              </div>

              {/* Questions Section */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Questions ({formData.questions.length})</h3>
                
                {/* Existing Questions */}
                {formData.questions.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {formData.questions.map((q, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="font-medium text-sm">Q{idx + 1}: {q.question}</p>
                          <p className="text-xs text-gray-600 mt-1">
                            {q.options.length} options | Category: {q.category}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeQuestion(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Question */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Add New Question</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Question Text *</Label>
                      <Input
                        value={currentQuestion.question}
                        onChange={(e) => setCurrentQuestion(prev => ({...prev, question: e.target.value}))}
                        placeholder="Enter your question"
                      />
                    </div>

                    <div>
                      <Label>Category</Label>
                      <Select 
                        value={currentQuestion.category} 
                        onValueChange={(val) => setCurrentQuestion(prev => ({...prev, category: val}))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="interests">Interests</SelectItem>
                          <SelectItem value="academics">Academics</SelectItem>
                          <SelectItem value="aptitude">Aptitude</SelectItem>
                          <SelectItem value="skills">Skills</SelectItem>
                          <SelectItem value="career">Career</SelectItem>
                          <SelectItem value="motivation">Motivation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Answer Options *</Label>
                      {currentQuestion.options.map((opt, idx) => (
                        <Input
                          key={idx}
                          value={opt}
                          onChange={(e) => {
                            const newOptions = [...currentQuestion.options];
                            newOptions[idx] = e.target.value;
                            setCurrentQuestion(prev => ({...prev, options: newOptions}));
                          }}
                          placeholder={`Option ${idx + 1}`}
                        />
                      ))}
                    </div>

                    <div>
                      <Label>Correct Answer (Optional for career assessments)</Label>
                      <Input
                        value={currentQuestion.correct_answer}
                        onChange={(e) => setCurrentQuestion(prev => ({...prev, correct_answer: e.target.value}))}
                        placeholder="Enter the correct answer"
                      />
                    </div>

                    <Button onClick={addQuestion} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Question
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                <Save className="w-4 h-4 mr-2" />
                {editingAssessment ? "Update" : "Create"} Assessment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default AssessmentManagement;
